import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Share2, Link, MessageCircle, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Gallery = () => {
  const [apartments, setApartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApartment, setSelectedApartment] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [expandedApartments, setExpandedApartments] = useState({});
  const [shareMenuOpen, setShareMenuOpen] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [sectionContent, setSectionContent] = useState({
    title: 'Our Apartments',
    subtitle: 'Discover Your Perfect Stay',
    description: 'Explore our collection of beautifully furnished serviced apartments, each designed to provide you with the comfort and convenience of home.'
  });

  useEffect(() => {
    fetchApartments();
    fetchSectionContent();
  }, []);

  const fetchSectionContent = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/settings/content/gallery`);
      if (response.data.content) {
        setSectionContent(response.data.content);
      }
    } catch (error) {
      console.error('Error fetching section content:', error);
    }
  };

  // Scroll to specific apartment if URL has hash
  useEffect(() => {
    if (apartments.length > 0) {
      const hash = window.location.hash;
      if (hash && hash.startsWith('#apartment-')) {
        const apartmentId = hash.replace('#apartment-', '');
        const element = document.getElementById(`apartment-${apartmentId}`);
        if (element) {
          setTimeout(() => {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            // Add highlight effect
            element.classList.add('ring-4', 'ring-amber-400', 'ring-offset-2');
            setTimeout(() => {
              element.classList.remove('ring-4', 'ring-amber-400', 'ring-offset-2');
            }, 2000);
          }, 500);
        }
      }
    }
  }, [apartments]);

  // Close share menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setShareMenuOpen(null);
    if (shareMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [shareMenuOpen]);

  const fetchApartments = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/apartments`);
      setApartments(response.data.apartments || []);
    } catch (error) {
      console.error('Failed to load apartments:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (apartmentId) => {
    setExpandedApartments(prev => ({
      ...prev,
      [apartmentId]: !prev[apartmentId]
    }));
  };

  const getVisibleImages = (apartment) => {
    const isExpanded = expandedApartments[apartment.id];
    if (!apartment.images || apartment.images.length === 0) return [];
    
    if (isExpanded || apartment.images.length <= 4) {
      return apartment.images;
    }
    
    return apartment.images.slice(0, 4);
  };

  const openLightbox = (apartment, imageIndex) => {
    setSelectedApartment(apartment);
    setSelectedImageIndex(imageIndex);
    setLightboxImage(apartment.images[imageIndex]);
  };

  const closeLightbox = () => {
    setLightboxImage(null);
    setSelectedApartment(null);
    setSelectedImageIndex(0);
  };

  const goToPrevious = () => {
    if (selectedApartment) {
      const newIndex = selectedImageIndex === 0 
        ? selectedApartment.images.length - 1 
        : selectedImageIndex - 1;
      setSelectedImageIndex(newIndex);
      setLightboxImage(selectedApartment.images[newIndex]);
    }
  };

  const goToNext = () => {
    if (selectedApartment) {
      const newIndex = selectedImageIndex === selectedApartment.images.length - 1 
        ? 0 
        : selectedImageIndex + 1;
      setSelectedImageIndex(newIndex);
      setLightboxImage(selectedApartment.images[newIndex]);
    }
  };

  const getShareUrl = (apartment) => {
    return `${window.location.origin}/#apartment-${apartment.id}`;
  };

  const getShareText = (apartment) => {
    return `Check out ${apartment.name} at Marina Homes! ${apartment.type} - Beautiful serviced apartment in Dar es Salaam.`;
  };

  const handleShare = async (apartment, method) => {
    const url = getShareUrl(apartment);
    const text = getShareText(apartment);

    if (method === 'native' && navigator.share) {
      try {
        await navigator.share({
          title: `${apartment.name} - Marina Homes`,
          text: text,
          url: url,
        });
        toast.success('Shared successfully!');
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    } else if (method === 'whatsapp') {
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text + '\n\n' + url)}`;
      window.open(whatsappUrl, '_blank');
      toast.success('Opening WhatsApp...');
    } else if (method === 'copy') {
      const textToCopy = url;
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(textToCopy);
        } else {
          // Fallback for non-secure contexts
          const textArea = document.createElement('textarea');
          textArea.value = textToCopy;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        }
        setCopiedLink(true);
        toast.success('Link copied to clipboard!');
        setTimeout(() => setCopiedLink(false), 2000);
      } catch (err) {
        // Show the link in a prompt as last resort
        window.prompt('Copy this link:', textToCopy);
      }
    }
    setShareMenuOpen(null);
  };

  const toggleShareMenu = (e, apartmentId) => {
    e.stopPropagation();
    setShareMenuOpen(shareMenuOpen === apartmentId ? null : apartmentId);
  };

  if (loading) {
    return (
      <section id="gallery" className="py-20 bg-gradient-to-b from-white to-amber-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading apartments...</p>
        </div>
      </section>
    );
  }

  return (
    <section id="gallery" className="py-6 bg-gradient-to-b from-white to-amber-50/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-5">
          <h2 className="text-2xl md:text-3xl font-bold text-amber-900 mb-1">
            {sectionContent.title}
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-sm">
            {sectionContent.description}
          </p>
        </div>

        {apartments.length === 0 ? (
          <div className="text-center py-8 bg-white rounded-lg shadow">
            <p className="text-gray-600 mb-2">No apartments available at the moment.</p>
            <p className="text-gray-500 text-sm">Please check back soon!</p>
          </div>
        ) : (
          <>
            {/* Apartments Grid */}
            <div className="space-y-6">
              {apartments.map((apartment) => {
                const visibleImages = getVisibleImages(apartment);
                const hasMoreImages = apartment.images && apartment.images.length > 4;
                const isExpanded = expandedApartments[apartment.id];
                const remainingCount = apartment.images ? apartment.images.length - 4 : 0;

                return (
                  <div 
                    key={apartment.id}
                    id={`apartment-${apartment.id}`}
                    className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 border border-amber-100"
                  >
                    {/* Apartment Header */}
                    <div className="bg-gradient-to-r from-amber-600 to-amber-700 px-4 py-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-xl font-bold text-white">{apartment.name}</h3>
                          <p className="text-amber-100">{apartment.type}</p>
                        </div>
                        
                        {/* Share Button */}
                        <div className="relative">
                          <button
                            onClick={(e) => toggleShareMenu(e, apartment.id)}
                            className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-all duration-200 hover:scale-110"
                            data-testid={`share-btn-${apartment.id}`}
                            title="Share this apartment"
                          >
                            <Share2 size={20} />
                          </button>
                          
                          {/* Share Dropdown Menu */}
                          {shareMenuOpen === apartment.id && (
                            <div 
                              className="absolute right-0 top-12 bg-white rounded-lg shadow-xl border border-gray-200 py-2 min-w-[180px] z-20"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <p className="px-4 py-1 text-xs text-gray-500 font-medium uppercase tracking-wide">Share via</p>
                              
                              {/* Native Share (Mobile) */}
                              {navigator.share && (
                                <button
                                  onClick={() => handleShare(apartment, 'native')}
                                  className="w-full px-4 py-2 text-left text-gray-700 hover:bg-amber-50 flex items-center space-x-3 transition-colors"
                                >
                                  <Share2 size={18} className="text-amber-600" />
                                  <span>Share</span>
                                </button>
                              )}
                              
                              {/* WhatsApp */}
                              <button
                                onClick={() => handleShare(apartment, 'whatsapp')}
                                className="w-full px-4 py-2 text-left text-gray-700 hover:bg-green-50 flex items-center space-x-3 transition-colors"
                              >
                                <MessageCircle size={18} className="text-green-600" />
                                <span>WhatsApp</span>
                              </button>
                              
                              {/* Copy Link */}
                              <button
                                onClick={() => handleShare(apartment, 'copy')}
                                className="w-full px-4 py-2 text-left text-gray-700 hover:bg-blue-50 flex items-center space-x-3 transition-colors"
                              >
                                {copiedLink ? (
                                  <>
                                    <Check size={18} className="text-green-600" />
                                    <span className="text-green-600">Copied!</span>
                                  </>
                                ) : (
                                  <>
                                    <Link size={18} className="text-blue-600" />
                                    <span>Copy Link</span>
                                  </>
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Apartment Images */}
                    <div className="p-4">
                      {apartment.images && apartment.images.length > 0 ? (
                        <>
                          <div className={`grid gap-3 ${
                            visibleImages.length === 1 ? 'grid-cols-1' :
                            visibleImages.length === 2 ? 'grid-cols-2' :
                            visibleImages.length === 3 ? 'grid-cols-3' :
                            'grid-cols-2 sm:grid-cols-4'
                          }`}>
                            {visibleImages.map((image, imageIndex) => (
                              <div
                                key={imageIndex}
                                className="relative group cursor-pointer overflow-hidden rounded-lg bg-gray-100"
                                style={{ aspectRatio: '4/3', maxHeight: '200px' }}
                                onClick={() => openLightbox(apartment, imageIndex)}
                              >
                                <img 
                                  src={image.url}
                                  alt={image.alt}
                                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                  <div className="absolute bottom-2 left-2 right-2">
                                    <p className="text-white text-xs font-medium">{image.alt}</p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* View More/Less Button */}
                          {hasMoreImages && (
                            <div className="mt-4 text-center">
                              <button
                                onClick={() => toggleExpanded(apartment.id)}
                                className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-300 hover:scale-105 shadow-md"
                              >
                                {isExpanded 
                                  ? 'Show Less' 
                                  : `View More (${remainingCount}+)`
                                }
                              </button>
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="text-gray-500 text-center py-4 text-sm">No images available</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Summary Stats */}
            <div className="mt-8 grid grid-cols-3 gap-3 text-center">
              <div className="bg-white p-3 rounded-lg shadow-sm border border-amber-100">
                <div className="text-2xl font-bold text-amber-700">{apartments.length}</div>
                <div className="text-gray-600 text-xs font-medium">Apartments</div>
              </div>
              <div className="bg-white p-3 rounded-lg shadow-sm border border-amber-100">
                <div className="text-2xl font-bold text-amber-700">
                  {new Set(apartments.map(a => a.type)).size}
                </div>
                <div className="text-gray-600 text-xs font-medium">Types</div>
              </div>
              <div className="bg-white p-3 rounded-lg shadow-sm border border-amber-100">
                <div className="text-2xl font-bold text-amber-700">100%</div>
                <div className="text-gray-600 text-xs font-medium">Satisfaction</div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Lightbox Modal */}
      {lightboxImage && selectedApartment && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button 
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-amber-400 transition-colors z-10"
          >
            <X size={32} />
          </button>

          <button 
            onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
            className="absolute left-4 text-white hover:text-amber-400 transition-colors z-10"
          >
            <ChevronLeft size={40} />
          </button>

          <button 
            onClick={(e) => { e.stopPropagation(); goToNext(); }}
            className="absolute right-4 text-white hover:text-amber-400 transition-colors z-10"
          >
            <ChevronRight size={40} />
          </button>

          <div 
            className="max-w-5xl max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <img 
              src={lightboxImage.url}
              alt={lightboxImage.alt}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
            <div className="text-center mt-4">
              <p className="text-amber-400 text-xl font-bold">{selectedApartment.name} - {selectedApartment.type}</p>
              <p className="text-white text-lg mt-2">{lightboxImage.alt}</p>
              <p className="text-gray-400 text-sm mt-2">
                Image {selectedImageIndex + 1} of {selectedApartment.images.length}
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
